/**
 * LibraryBrowse — Phase 124.E. Tile-based browse mode for the
 * My Library page. Replaces the bare "Unfiled" landing with:
 *
 *   1. <SubjectTilesBrowse />  — landing view. Big subject-folder
 *      tiles + an Unfiled tile + a small "Recent activity" strip.
 *   2. <UnitTilesStrip />      — child Unit folder tiles shown ABOVE
 *      loose-in-Subject activities when the teacher drills into a
 *      Subject folder.
 *
 * Tiles render in the folder's chosen colour (full-pill background).
 * Click a tile → caller switches selectedFolderId so the existing
 * folder view (or this same component, recursively for Subjects)
 * takes over.
 */
import { useState } from "react";
import { Folder, FolderTree, Plus, X, MoreHorizontal, Trash2, Calculator, Check, CheckSquare, FolderInput, ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { formatRelativeTime } from "../lib/relativeTime";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

// Heuristic: which folder colours need light vs. dark text for legibility.
// Current FOLDER_PALETTE is all light pastels → always dark text. We still
// compute it dynamically so future palette additions don't break readability.
const _needsLightText = (hex) => {
  if (!hex || typeof hex !== "string") return false;
  const m = /^#?([a-f0-9]{6})$/i.exec(hex);
  if (!m) return false;
  const v = m[1];
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  // YIQ brightness — anything darker than ~128 needs white text.
  return (r * 299 + g * 587 + b * 114) / 1000 < 140;
};

const UNIT_COLORS = [
  "#96ded1", // mint
  "#a8d0e6", // sky
  "#f6c89f", // peach
  "#d8b4f8", // lavender
  "#bce5a3", // sage
  "#f5b7b1", // blush
];

const UnitComposer = ({ onCreate, onCancel, testidPrefix = "library-browse-unit-compose" }) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState(UNIT_COLORS[0]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onCreate(trimmed, color);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`${testidPrefix}-form`}
      className="ae-card flex flex-wrap items-center gap-2 p-3 border-l-4 border-l-[#96ded1] bg-[#96ded1]/10"
    >
      <input
        autoFocus
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Unit name (e.g. Fractions)"
        data-testid={`${testidPrefix}-name`}
        className="flex-1 min-w-[180px] h-9 rounded-md border border-[#4a6984]/25 bg-white px-3 text-sm font-semibold text-[#1c2d3d] placeholder:text-[#4a6984]/60 focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
      />
      <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Unit colour">
        {UNIT_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setColor(c)}
            data-testid={`${testidPrefix}-color-${c.replace("#", "")}`}
            aria-label={`Pick colour ${c}`}
            aria-pressed={color === c}
            className={`h-6 w-6 rounded-md border-2 transition-transform ${
              color === c ? "border-[#1c2d3d] scale-110" : "border-transparent hover:scale-105"
            }`}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      <button
        type="submit"
        disabled={!name.trim() || busy}
        data-testid={`${testidPrefix}-submit`}
        className="h-9 px-3 rounded-md bg-[#1c2d3d] text-white text-xs font-extrabold uppercase tracking-wide disabled:opacity-50 hover:bg-[#4a6984] transition-colors"
      >
        {busy ? "Creating…" : "Create unit"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        data-testid={`${testidPrefix}-cancel`}
        className="h-9 px-2 rounded-md text-[#4a6984] hover:bg-[#f4f5f7] inline-flex items-center"
        aria-label="Cancel new unit"
      >
        <X className="h-4 w-4" />
      </button>
    </form>
  );
};

const FolderTile = ({
  color,
  title,
  subtitle,
  meta,
  onClick,
  testid,
  icon: Icon = Folder,
  cornerAction = null,
  selectMode = false,
  selectable = false,
  selected = false,
  onToggleSelect,
}) => {
  const light = _needsLightText(color);
  const textColor = light ? "#ffffff" : "#1c2d3d";
  const subColor = light ? "rgba(255,255,255,0.85)" : "rgba(28,45,61,0.7)";
  const metaColor = light ? "rgba(255,255,255,0.7)" : "rgba(28,45,61,0.55)";
  const inSelect = selectMode && selectable;
  const handleClick = inSelect ? () => onToggleSelect?.() : onClick;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        data-testid={testid}
        aria-pressed={inSelect ? selected : undefined}
        className={`group w-full text-left rounded-2xl p-5 min-h-[140px] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c2d3d] focus-visible:ring-offset-2 ${
          inSelect && selected ? "ring-2 ring-[#1c2d3d] ring-offset-2" : ""
        }`}
        style={{
          backgroundColor: color || "#96ded1",
          color: textColor,
        }}
      >
        <div className="flex items-start gap-3">
          <span
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl shrink-0"
            style={{
              backgroundColor: light ? "rgba(255,255,255,0.18)" : "rgba(28,45,61,0.12)",
            }}
          >
            <Icon className="h-5 w-5" style={{ color: textColor }} />
          </span>
          <div className="min-w-0">
            <p
              className="font-black text-lg leading-tight truncate"
              style={{ color: textColor }}
              title={title}
            >
              {title}
            </p>
            {subtitle ? (
              <p
                className="mt-0.5 text-xs font-semibold uppercase tracking-[0.16em]"
                style={{ color: subColor }}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
        {meta ? (
          <p
            className="absolute bottom-4 left-5 right-5 text-[11px] font-bold uppercase tracking-[0.18em] truncate"
            style={{ color: metaColor }}
          >
            {meta}
          </p>
        ) : null}
      </button>
      {inSelect ? (
        <div
          data-testid={`${testid}-checkbox`}
          aria-checked={selected}
          role="checkbox"
          className={`pointer-events-none absolute top-3 right-3 inline-flex h-7 w-7 items-center justify-center rounded-md border-2 ${
            selected ? "bg-[#1c2d3d] border-[#1c2d3d]" : "bg-white/85 border-white"
          }`}
        >
          {selected && <Check className="h-4 w-4 text-[#96ded1]" />}
        </div>
      ) : (
        cornerAction
      )}
    </div>
  );
};

/**
 * Small kebab "more" menu rendered at the corner of a folder tile.
 * Today it only carries Delete (the cascade-delete dialog lives in
 * Dashboard and handles confirmation + the three cascade modes), but
 * the menu is structured so Rename / colour can be added later
 * without re-jigging tile layout.
 *
 * Stops event propagation on its own click so opening the menu (or
 * clicking Delete) never accidentally also opens the folder.
 */
const TileMoreMenu = ({ folder, onDelete, light, testidPrefix }) => {
  if (!onDelete) return null;
  return (
    <div
      className="absolute top-3 left-3"
      onClick={(e) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid={`${testidPrefix}-more`}
            aria-label={`More actions for ${folder.name}`}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors"
            style={{
              backgroundColor: light ? "rgba(255,255,255,0.22)" : "rgba(28,45,61,0.12)",
              color: light ? "#ffffff" : "#1c2d3d",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="bg-white border-[#4a6984]/15 min-w-[170px]"
        >
          <DropdownMenuItem
            onSelect={() => onDelete(folder.id)}
            data-testid={`${testidPrefix}-delete`}
            className="cursor-pointer text-red-600 focus:text-red-700"
          >
            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete folder
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

/**
 * Subject-folder tiles + Unfiled tile + Recent activity strip. This is
 * the landing view for the My Library tab.
 */
export const SubjectTilesBrowse = ({
  folders,
  activities,
  onSelectFolder,
  onCreateFolder,
  onAddUnitToSubject,
  onDeleteFolder,
  onDeleteActivity,
  onMoveActivity,
  unfiledCount,
  selectMode = false,
  onToggleSelectMode,
  selectedFolderIds,
  onToggleSelectFolder,
  selectedActivityIds,
  onToggleSelectActivity,
}) => {
  const [composerSubjectId, setComposerSubjectId] = useState(null);
  const [unfiledCollapsed, setUnfiledCollapsed] = useState(false);
  const subjects = (folders || []).filter((f) => !f.parent_folder_id && !f.system);
  const systemFolder = (folders || []).find((f) => f.system) || null;
  const units = (folders || []).filter((f) => !!f.parent_folder_id);
  const unitsBySubject = new Map();
  units.forEach((u) => {
    if (!unitsBySubject.has(u.parent_folder_id)) {
      unitsBySubject.set(u.parent_folder_id, []);
    }
    unitsBySubject.get(u.parent_folder_id).push(u);
  });

  const countFor = (fid) =>
    (activities || []).filter((a) => a.folder_id === fid).length;

  // "Unfiled lessons" — every activity not filed into any folder, newest
  // first, shown in full below the Subject folders as a vertical list.
  const unfiled = (activities || [])
    .filter((a) => !a.folder_id)
    .slice()
    .sort((a, b) => {
      const tb = Date.parse(b?.created_at || "") || 0;
      const ta = Date.parse(a?.created_at || "") || 0;
      return tb - ta;
    });

  return (
    <div data-testid="library-browse-tiles" className="space-y-8">
      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
            Subjects
          </p>
          <div className="flex items-center gap-2">
            {onToggleSelectMode ? (
              <button
                type="button"
                data-testid={selectMode ? "library-browse-select-cancel" : "library-browse-select-enter"}
                onClick={onToggleSelectMode}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:text-[#1c2d3d] border border-[#4a6984]/20 hover:bg-[#f4f5f7] transition-colors"
              >
                {selectMode ? (
                  <><X className="h-3 w-3" /> Cancel</>
                ) : (
                  <><CheckSquare className="h-3 w-3" /> Select</>
                )}
              </button>
            ) : null}
            <button
              type="button"
              data-testid="library-browse-new-subject"
              onClick={onCreateFolder}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:text-[#1c2d3d] border border-[#4a6984]/20 hover:bg-[#f4f5f7] transition-colors"
            >
              <Plus className="h-3 w-3" /> New subject
            </button>
          </div>
        </div>
        {subjects.length === 0 && !systemFolder && unfiledCount === 0 ? (
          <div
            className="ae-card p-8 text-center"
            data-testid="library-browse-empty-subjects"
          >
            <p className="font-extrabold text-[#1c2d3d]">
              No Subject folders yet
            </p>
            <p className="mt-2 text-sm text-[#4a6984]/75">
              Subjects sit at the top of your library. Add one with{" "}
              <span className="font-bold">+ New subject</span> — e.g. Math,
              Language, Science.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="library-browse-subject-grid"
          >
            {subjects.map((f) => {
              const childUnits = unitsBySubject.get(f.id) || [];
              const loose = countFor(f.id);
              const nested = childUnits.reduce(
                (sum, c) => sum + countFor(c.id),
                0,
              );
              const total = loose + nested;
              const light = _needsLightText(f.color);
              return (
                <div key={f.id} className="space-y-2">
                  <FolderTile
                    color={f.color}
                    icon={FolderTree}
                    title={f.name}
                    subtitle={
                      childUnits.length === 0
                        ? "Subject"
                        : `${childUnits.length} unit${childUnits.length === 1 ? "" : "s"}`
                    }
                    meta={`${total} item${total === 1 ? "" : "s"}`}
                    onClick={() => onSelectFolder(f.id)}
                    testid={`library-browse-subject-${f.id}`}
                    selectMode={selectMode}
                    selectable
                    selected={!!selectedFolderIds?.has(f.id)}
                    onToggleSelect={() => onToggleSelectFolder?.(f.id)}
                    cornerAction={
                      <>
                        <TileMoreMenu
                          folder={f}
                          onDelete={onDeleteFolder}
                          light={light}
                          testidPrefix={`library-browse-subject-${f.id}`}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setComposerSubjectId((cur) => (cur === f.id ? null : f.id));
                          }}
                          data-testid={`library-browse-subject-${f.id}-add-unit`}
                          aria-label={`Add a unit to ${f.name}`}
                          title="Add a unit"
                          className="absolute top-3 right-3 inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] font-extrabold uppercase tracking-wide transition-colors"
                          style={{
                            backgroundColor: light ? "rgba(255,255,255,0.22)" : "rgba(28,45,61,0.12)",
                            color: light ? "#ffffff" : "#1c2d3d",
                          }}
                        >
                          <Plus className="h-3 w-3" /> Unit
                        </button>
                      </>
                    }
                  />
                  {composerSubjectId === f.id ? (
                    <UnitComposer
                      testidPrefix={`library-browse-subject-${f.id}-compose`}
                      onCancel={() => setComposerSubjectId(null)}
                      onCreate={async (name, color) => {
                        await onAddUnitToSubject(f.id, name, color);
                        setComposerSubjectId(null);
                      }}
                    />
                  ) : null}
                </div>
              );
            })}
            {systemFolder ? (
              <FolderTile
                color={systemFolder.color}
                icon={Calculator}
                title={systemFolder.name}
                subtitle="Managed by AtomicEdu"
                meta={`${countFor(systemFolder.id)} item${countFor(systemFolder.id) === 1 ? "" : "s"}`}
                onClick={() => onSelectFolder(systemFolder.id)}
                testid="library-browse-math-engine-tile"
              />
            ) : null}
          </div>
        )}
      </div>

      {unfiled.length > 0 ? (
        <div>
          <button
            type="button"
            data-testid="library-browse-unfiled-toggle"
            onClick={() => setUnfiledCollapsed((v) => !v)}
            aria-expanded={!unfiledCollapsed}
            className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/80 hover:text-[#1c2d3d] transition-colors"
          >
            {unfiledCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            Unfiled lessons
            <span className="inline-flex items-center justify-center min-w-[1.4rem] h-5 px-1.5 rounded-full bg-[#4a6984]/10 text-[#1c2d3d] text-[11px] font-extrabold tracking-normal">
              {unfiled.length}
            </span>
          </button>
          {!unfiledCollapsed && (
          <div className="space-y-2" data-testid="library-browse-unfiled-list">
            {unfiled.map((a) => {
              const rowSelected = !!selectedActivityIds?.has(a.id);
              if (selectMode) {
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`library-browse-unfiled-row-${a.id}`}
                    onClick={() => onToggleSelectActivity?.(a.id)}
                    aria-pressed={rowSelected}
                    className={`ae-card w-full text-left flex items-center gap-3 p-3 transition-colors ${
                      rowSelected ? "ring-2 ring-[#96ded1] bg-[#96ded1]/10" : "hover:border-[#1c2d3d]/30"
                    }`}
                  >
                    <span
                      data-testid={`library-browse-unfiled-checkbox-${a.id}`}
                      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${
                        rowSelected ? "bg-[#96ded1] border-[#1c2d3d]" : "bg-white border-[#4a6984]/30"
                      }`}
                    >
                      {rowSelected && <Check className="h-4 w-4 text-[#1c2d3d]" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-[#1c2d3d] truncate">
                        {a.activity_name || "Untitled"}
                      </p>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a6984]/70 mt-0.5">
                        {(a.output_type || "lesson").replace(/_/g, " ")}
                      </p>
                    </div>
                  </button>
                );
              }
              return (
                <div
                  key={a.id}
                  data-testid={`library-browse-unfiled-row-${a.id}`}
                  className="ae-card flex items-stretch gap-1 p-0 hover:border-[#1c2d3d]/30 transition-colors overflow-hidden"
                >
                  <Link
                    to={`/lesson/${a.id}`}
                    data-testid={`library-browse-unfiled-open-${a.id}`}
                    className="flex-1 min-w-0 flex items-center justify-between gap-3 p-3 no-underline"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#1c2d3d] truncate">
                        {a.activity_name || "Untitled"}
                      </p>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a6984]/70 mt-0.5">
                        {(a.output_type || "lesson").replace(/_/g, " ")}
                      </p>
                    </div>
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#4a6984]/70">
                      {formatRelativeTime(a.created_at)}
                    </span>
                  </Link>
                  {onMoveActivity && subjects.length > 0 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`library-browse-unfiled-file-${a.id}`}
                          aria-label={`File ${a.activity_name || "lesson"} into a subject`}
                          title="File into…"
                          className="shrink-0 inline-flex items-center gap-1 px-3 text-[11px] font-extrabold uppercase tracking-wide text-[#4a6984]/80 hover:text-[#1c2d3d] hover:bg-[#96ded1]/20 border-l border-[#4a6984]/10 transition-colors"
                        >
                          <FolderInput className="h-4 w-4" /> File
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="max-h-72 overflow-y-auto"
                        data-testid={`library-browse-file-menu-${a.id}`}
                      >
                        <DropdownMenuLabel>File into…</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {subjects.map((s) => {
                          const subjUnits = unitsBySubject.get(s.id) || [];
                          return (
                            <div key={s.id}>
                              <DropdownMenuItem
                                data-testid={`library-browse-file-target-${a.id}-${s.id}`}
                                onClick={() => onMoveActivity(a.id, s.id)}
                              >
                                <Folder className="h-4 w-4 mr-2 shrink-0" />
                                <span className="truncate">{s.name}</span>
                              </DropdownMenuItem>
                              {subjUnits.map((u) => (
                                <DropdownMenuItem
                                  key={u.id}
                                  data-testid={`library-browse-file-target-${a.id}-${u.id}`}
                                  onClick={() => onMoveActivity(a.id, u.id)}
                                  className="pl-8"
                                >
                                  <FolderTree className="h-3.5 w-3.5 mr-2 shrink-0 opacity-70" />
                                  <span className="truncate text-[13px]">{u.name}</span>
                                </DropdownMenuItem>
                              ))}
                            </div>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                  {onDeleteActivity ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDeleteActivity(a);
                      }}
                      data-testid={`library-browse-unfiled-delete-${a.id}`}
                      aria-label={`Delete ${a.activity_name || "lesson"}`}
                      title="Delete lesson"
                      className="shrink-0 inline-flex items-center justify-center px-3 text-[#4a6984]/70 hover:text-rose-700 hover:bg-rose-50 border-l border-[#4a6984]/10 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Strip of child Unit folder tiles rendered ABOVE loose-in-Subject
 * activities when the teacher is viewing a Subject folder.
 */
export const UnitTilesStrip = ({
  units,
  activities,
  onSelectFolder,
  onAddUnit,
  onDeleteFolder,
  subjectName,
  selectMode = false,
  selectedFolderIds,
  onToggleSelectFolder,
}) => {
  const [composing, setComposing] = useState(false);
  const hasUnits = units && units.length > 0;
  if (!hasUnits && !onAddUnit) return null;
  const countFor = (fid) =>
    (activities || []).filter((a) => a.folder_id === fid).length;
  return (
    <div className="mb-6 space-y-3" data-testid="library-browse-unit-strip">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
          Units{subjectName ? ` in ${subjectName}` : ""}
        </p>
        {onAddUnit ? (
          <button
            type="button"
            onClick={() => setComposing((v) => !v)}
            data-testid="library-browse-unit-strip-add"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:text-[#1c2d3d] border border-[#4a6984]/20 hover:bg-[#f4f5f7] transition-colors"
          >
            {composing ? (
              <><X className="h-3 w-3" /> Cancel</>
            ) : (
              <><Plus className="h-3 w-3" /> New unit</>
            )}
          </button>
        ) : null}
      </div>
      {composing && onAddUnit ? (
        <UnitComposer
          testidPrefix="library-browse-unit-strip-compose"
          onCancel={() => setComposing(false)}
          onCreate={async (name, color) => {
            await onAddUnit(name, color);
            setComposing(false);
          }}
        />
      ) : null}
      {hasUnits ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {units.map((u) => (
            <FolderTile
              key={u.id}
              color={u.color}
              title={u.name}
              subtitle="Unit"
              meta={`${countFor(u.id)} item${countFor(u.id) === 1 ? "" : "s"}`}
              onClick={() => onSelectFolder(u.id)}
              testid={`library-browse-unit-${u.id}`}
              selectMode={selectMode}
              selectable
              selected={!!selectedFolderIds?.has(u.id)}
              onToggleSelect={() => onToggleSelectFolder?.(u.id)}
              cornerAction={
                <TileMoreMenu
                  folder={u}
                  onDelete={onDeleteFolder}
                  light={_needsLightText(u.color)}
                  testidPrefix={`library-browse-unit-${u.id}`}
                />
              }
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SubjectTilesBrowse;
